// GET /v1.0/audit-log - paged change history for one audited record (PAUL-3369). Additive: the
// subscription instance payload still carries `status_changes` and the timeline built from it is
// untouched, so a status change now shows up in both places. This is the superset.

// The audited record kinds the endpoint accepts. Sent as-is on the `entity_type` query param, which
// is required - an unknown value is a 400 `audit_log_entity_type_unknown`, never an empty page.
export type AuditLogEntityType = 'subscription_instance' | 'subscription_mandate' | 'product' | 'discount';

export enum AuditLogAction {
  Created = 0,
  Updated = 1,
  // The row describes the record, not a field: `changes` is empty.
  Deleted = 2,
  // A soft-deleted record put back; `changes` carries whatever else that same save altered.
  Restored = 3,
}

export enum AuditActorKind {
  // No signed-in caller: a nightly sweep, an import, a payment webhook.
  System = 0,
  // A moderator/admin/POS user. Under impersonation this is the impersonator, not the impersonated.
  Staff = 1,
  // The customer acting on their own record, through the portal.
  Customer = 2,
}

export interface AuditLogChangeModel {
  /* The wire field name this entity's own endpoint serves (`billing_period_type`), deliberately not
     a human label - the edit form already has a translated label for this exact key. */
  property_name: string;
  // Null on Created.
  old_value?: string | null;
  // Null on Deleted.
  new_value?: string | null;
}

/* One row per save, not per field: a single edit that touched eight fields is one row carrying
   eight changes. */
export interface AuditLogEntryListModel {
  id: string;
  entity_type: AuditLogEntityType;
  entity_id: string;
  action: AuditLogAction;
  changes: AuditLogChangeModel[];
  actor_kind: AuditActorKind;
  // All three null for a system change - render "System", not a blank.
  changed_by_id?: string | null;
  changed_by_name?: string | null;
  changed_by_email?: string | null;
  // Free text an operator typed, when the change carried one.
  reason?: string | null;
  timestamp: string;
}

/* `action` and `actor_kind` are asymmetric: the response serialises them as integers, but the
   filters bind the enum NAME (`Updated`, `Staff`). Anything unparseable is a 400, so the names are
   taken from the enum objects rather than typed out. */
export interface GetAuditLogQuery {
  entity_type: AuditLogEntityType;
  entity_id: string;
  property_name?: string;
  action?: keyof typeof AuditLogAction;
  actor_kind?: keyof typeof AuditActorKind;
  from?: string;
  to?: string;
  day?: string;
  /* Actor name/email, changed field names and the reason - NOT values, which live in a compressed
     blob SQL cannot read into. A term matching a value on screen finding nothing is expected.

     The wire name is `search.value`, not `search`: the backend binds a nested SortSearchSearch
     object (SortSearchModelBase), so a flat `search` binds nothing and is dropped without error. */
  'search.value'?: string;
  order_column_name?: string;
  order_column_descending?: boolean;
  start?: number;
  // Omitting it means 25, not everything. Capped server-side at 200.
  length?: number;
  [key: string]: unknown;
}
